import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../Workflow.scss'
import WorkflowTask from '../../../../../components/library/workflow-tasks/WorkflowTask'
import { useDispatch } from 'react-redux'
import TicketActions from '../../../../../store/ticketRedux';
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import { WorkflowTasks } from '../../../services/WorkflowTasks';
import WorkflowTaskDescriptionWithHelp from '../../../../../components/library/workflow-tasks/WorkflowTaskDescriptionWithHelp';
import VerticalNameChildren from '../../../../../components/library/layout/VerticalNameChildren';

import { TaskStatus } from '../../../services/TaskStatus';
import DateTime from '../../../../../components/library/common/DateTime';
import EmdashText from '../../../../../components/library/common/EmdashText';
import ViewDownloadDocument from '../../../../../components/library/documents/ViewDownloadDocument';
import TaskErrorText from '../../../../../components/library/workflow-tasks/TaskErrorText';
import Button from '../../../../../components/library/buttons/Button';
import WorkflowTaskResetModal from '../../../../../components/library/workflow-tasks/WorkflowTaskResetModal';
import { repairMissingInterviewFile, resetInterviewForTicket } from '../../../../../services/tickets/ticketResetter';
import { useTicketState } from '../../hooks/useTicketStateHook';
import { trackEvent, HeapEvents } from '../../../../../services/util/AnalyticsUtil';
import Skeleton from '../../../../../components/library/loaders/Skeleton';

const ClientInterviewTask = (props) => {
    const dispatch = useDispatch();
    const ticketIsOpen = useTicketState(props.ticket);
    const [baseStatus, completions] = useTaskStatus(props.ticket, [WorkflowTasks.INTERVIEW_COMPLETED]);
    const [preparePaymentStatus] = useTaskStatus(props.ticket, [WorkflowTasks.PAYMENT_PREPARED]);

    const [status, setStatus] = useState(TaskStatus.NOT_STARTED);
    const [completedDate, setCompletedDate] = useState(null);
    const [fileId, setFileId] = useState(null)
    const [resetModalShowing, setResetModalShowing] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [repairing, setRepairing] = useState(false);

    const ticketType = props.ticket.ticketType.identifier.toUpperCase(); 
    
    const handleShowModal = () => {
        setResetModalShowing(true)
    }

    const handleHideModal = () => {
        setResetModalShowing(false);
    }

    const handleResetInterview = async () => {
        setResetting(true);
        if (await resetInterviewForTicket(props.ticket.id)) {
            dispatch(TicketActions.getTicket(props.ticket.id))
            trackEvent(HeapEvents.INTERVIEW_RESET, props.ticket.id)
        }
        setResetting(false);
        setResetModalShowing(false);
    }

    const handleViewDownloadError = () => {
        setFileId(null);
    }

    const handleRepairInterviewTask = async () => {
        setRepairing(true);
        var fIdData = await repairMissingInterviewFile(props.ticket.id);
        if (fIdData) {
            setFileId(fIdData.fileId)
        }
        setRepairing(false);
    }

    useEffect(() => {
        if (baseStatus === TaskStatus.COMPLETE && (!fileId || !completedDate)) {
            if (completedDate && !fileId) {
                handleRepairInterviewTask();                
            }
            setStatus(TaskStatus.ERROR)
        } else {
            setStatus(baseStatus)
        }
    }, [baseStatus, fileId, completedDate])

    useEffect(() => {
        var validCompletion = (completions || []).find(x => x.status !== TaskStatus.INVALID);
        if (!validCompletion || !props.ticket) {
            setCompletedDate(null);
            setFileId(null);
        } else {
            var completionData = validCompletion.completionData ? JSON.parse(validCompletion.completionData) : {}
            setCompletedDate(validCompletion.lastModified)
            setFileId(completionData.FileId)            
        }
    }, [props.ticket])

    return (
        <WorkflowTask status={status} taskId={WorkflowTasks.INTERVIEW_COMPLETED}>
            <div className='columns'>
                {ticketType !== 'VIR' ? <WorkflowTaskDescriptionWithHelp
                    helpKey='0ProSupport/VDOPro/ClientInterview.htm'
                    descriptions={baseStatus === TaskStatus.COMPLETE
                        ? ['InterviewTask.Description1', 'InterviewTask.Description2.vdoORrte']
                        : ['InterviewTask.DescriptionAlt']} /> : 
                    <WorkflowTaskDescriptionWithHelp
                        helpKey='0ProSupport/VDOPro/ClientInterview.htm'
                        descriptions={baseStatus === TaskStatus.COMPLETE
                            ? ['InterviewTask.Description1', `InterviewTask.Description2.${ticketType.toLowerCase()}`]
                            : ['InterviewTask.DescriptionAlt']} />
                }
                
            </div>
            <WorkflowTaskResetModal
                isOpen={resetModalShowing} status={status} onCancel={handleHideModal}
                onReset={handleResetInterview} titleKey="InterviewTask.ResetModal.Title"
                resetButtonKey="InterviewTask.Reset"
                contentKeys={['InterviewTask.ResetModal.Content1', 'InterviewTask.ResetModal.Content2']} />
            <div className='columns'>
                <div className='column is-1' />
                <div className='column is-10'>
                    <VerticalNameChildren
                        textKey="InterviewTask.Completed"
                        loading={!props.ticket}>
                        {completedDate && baseStatus === TaskStatus.COMPLETE &&
                            <p className='p1 medium-weight'><DateTime value={completedDate} dateAndTime /></p>
                        }
                        {(props.ticket && baseStatus !== TaskStatus.COMPLETE) &&
                            <p className='p1 medium-weight'><EmdashText /></p>
                        }
                        {(props.ticket && !completedDate && baseStatus === TaskStatus.COMPLETE) &&
                            <TaskErrorText errorKey='InterviewTask.MissingTimestamp' />
                        }
                    </VerticalNameChildren>
                </div>
                <div className='column is-1' />
            </div>
            {(baseStatus === TaskStatus.COMPLETE || (fileId && baseStatus === TaskStatus.IN_PROGRESS)) &&
                <div className='columns level'>
                    <div className='column is-1' />
                <div className={ticketType !== 'VIR' ? 'column is-7' : 'column is-12'}>
                    {repairing &&
                        <Skeleton />
                    }
                    {!repairing && fileId &&
                        <ViewDownloadDocument ticketId={props.ticket.id} documentType='Interview'
                            onViewDownloadFail={handleViewDownloadError}/>
                    }
                    {!repairing && !fileId && 
                        <TaskErrorText errorKey='InterviewTask.MissingPDF' />
                    }

                </div>
                {
                        ticketType !== 'VIR' && preparePaymentStatus !== TaskStatus.COMPLETE ?
                    (<div className='column is-3'>
                        <Button
                            loading={resetting}
                            elementId='ticket-cancel-btn'
                            labelKey='InterviewTask.Reset'
                            buttonStyles='secondary'
                            disabled={!ticketIsOpen}
                            onClick={handleShowModal} />
                    </div>
                    ) : (<div className='column is-3'/>)}
                    <div className='column is-1' />
                
                </div>
            }
        </WorkflowTask>
    );
}

ClientInterviewTask.whyDidYouRender = false;

ClientInterviewTask.propTypes = {
    ticket: PropTypes.object
}

export default ClientInterviewTask